import React, { useCallback, memo } from 'react'
import {
  Table as BaseTable,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '../ui/Table'
import { useTableSort } from './hooks'
import { COLUMN_HEADER_MAX_WIDTH, DISPLAY_MODES } from './constants'
import EmptyState from '../ui/EmptyState'
import VariableCell from './VariableCell'

const getCellValue = (row, columnId, displayMode) => {
  const source = displayMode === DISPLAY_MODES.SATISFACTION ? row.satisfactions : row.grades
  return source?.[columnId] ?? null
}

const getGlobalValue = (row, displayMode) => {
  if (displayMode === DISPLAY_MODES.SATISFACTION)
    return row.globalSatisfaction
  return row.globalGrade
}

const formatGrade = value => {
  if (value === null || typeof value === 'undefined')
    return '-'
  return value.toFixed(1)
}

const formatSatisfaction = value => {
  if (value === null || typeof value === 'undefined')
    return '-'
  return `${value.toFixed(1)}%`
}

const GradeTable = ({ statistics, displayMode = DISPLAY_MODES.AVERAGE_GRADE, filterUrl, variableId }) => {
  const getSortValue = useCallback((row, column) => {
    if (column === 'value')
      return (row.value || '').toLowerCase()
    if (column === 'global')
      return getGlobalValue(row, displayMode) ?? -1
    return getCellValue(row, column, displayMode) ?? -1
  }, [displayMode])

  const { sortedRows, sortProps } = useTableSort(statistics?.rows, getSortValue)

  if (!statistics?.rows?.length)
    return <EmptyState />

  const multiColumn = statistics.columns.length > 1
  const formatValue = displayMode === DISPLAY_MODES.SATISFACTION ? formatSatisfaction : formatGrade
  const { columns } = statistics
  const globalLabel = displayMode === DISPLAY_MODES.SATISFACTION ? 'Taux global' : 'Note globale'

  return (
    <div className="mt-3">
      <BaseTable hover>
        <TableHead>
          <TableRow>
            <TableHeader {...sortProps('value')} className="text-nowrap">
              Variable
            </TableHeader>
            {multiColumn ? (
              <TableHeader className="text-center bg-light" {...sortProps('global')}>
                {globalLabel}
              </TableHeader>
            ) : null}
            {columns.map(column => (
              <TableHeader key={column.id} className="text-center" {...sortProps(column.id)}>
                <span
                  className="d-inline-block text-truncate align-middle"
                  style={COLUMN_HEADER_MAX_WIDTH}
                  title={column.name}
                >
                  {column.name}
                </span>
              </TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => (
            <TableRow key={row.value || `empty-${index}`}>
              <VariableCell filterUrl={filterUrl} row={row} variableId={variableId} />
              {multiColumn ? (
                <TableCell className="text-center font-weight-bold bg-light-hover">
                  {formatValue(getGlobalValue(row, displayMode))}
                </TableCell>
              ) : null}
              {columns.map(column => (
                <TableCell key={column.id} className="text-center">
                  {formatValue(getCellValue(row, column.id, displayMode))}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </BaseTable>
    </div>
  )
}

export default memo(GradeTable)
